if (!customElements.get('litmus-product-trust')) {
  customElements.define('litmus-product-trust', class extends HTMLElement {
    static verifiedProof(data, productId, now = Date.now()) {
      const count = (value) => Number.isSafeInteger(value) && value >= 0;
      if (!data || data.source !== 'shopify_orders' || data.complete_window !== true ||
          !count(data.product_id) || String(data.product_id) !== productId ||
          !count(data.as_of) || !count(data.window_start) || data.as_of - data.window_start !== 86400 ||
          data.as_of * 1000 > now || now - data.as_of * 1000 >= 7200000) return null;
      return {
        units: count(data.units_24h) ? data.units_24h : null,
        customers: data.customers_verified === true && count(data.customers) ? data.customers : null,
        popular: data.popular_offer_verified === true && data.popular_offer === 'monthly' &&
          data.popular_offer_basis === 'initial_subscription_choices',
        expires: (data.as_of + 7200) * 1000,
      };
    }

    connectedCallback() {
      this.disconnectedCallback();
      if (!this.hasAttribute('data-revealed')) {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.setAttribute('data-revealed', '');
        else {
          this.observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.setAttribute('data-revealed', '');
            this.observer.disconnect();
          }, { threshold: 0.25 });
          this.observer.observe(this.querySelector('.litmus-delivery') || this);
        }
      }
      const record = this.querySelector('[data-proof]');
      if (!record) return;
      this.applyProof(record.textContent);
      this.scheduleRefresh();
      this.onVisibility = () => {
        if (!document.hidden) {
          this.hideProof();
          this.refresh();
        }
      };
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.request?.abort();
      clearTimeout(this.refreshTimer);
      clearTimeout(this.expiryTimer);
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    hideProof() {
      clearTimeout(this.expiryTimer);
      const output = this.querySelector('[data-live-proof]');
      if (output) output.hidden = true;
    }

    applyProof(text) {
      this.hideProof();
      let proof;
      try { proof = this.constructor.verifiedProof(JSON.parse(text), this.dataset.productId); }
      catch { return; }
      if (!proof) return;
      const output = this.querySelector('[data-live-proof]');
      const previous = output.textContent;
      for (const key of ['units', 'customers']) {
        const row = output.querySelector(`[data-${key}]`);
        row.hidden = proof[key] === null;
        row.querySelector('[data-count]').textContent = proof[key] === null ? '' : proof[key].toLocaleString(document.documentElement.lang);
      }
      output.querySelector('[data-popular]').hidden = !proof.popular;
      output.hidden = proof.units === null && proof.customers === null && !proof.popular;
      if (!output.hidden && output.textContent !== previous) {
        output.classList.remove('litmus-trust-proof--updated');
        void output.offsetWidth;
        output.classList.add('litmus-trust-proof--updated');
      }
      this.expiryTimer = setTimeout(() => this.hideProof(), Math.max(0, proof.expires - Date.now()));
    }

    scheduleRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), 3600000);
    }

    async refresh() {
      clearTimeout(this.refreshTimer);
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      try {
        const url = new URL(location.href);
        const productPath = new URL(this.dataset.url, location.origin).pathname;
        if (!url.pathname.endsWith(productPath)) throw new Error('Product changed');
        url.searchParams.set('section_id', this.dataset.section);
        if (this.dataset.view) url.searchParams.set('view', this.dataset.view);
        const response = await fetch(url, { signal: request.signal, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Proof refresh failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = html.getElementById(this.id);
        const record = replacement?.querySelector('[data-proof]');
        if (!record || replacement.dataset.productId !== this.dataset.productId) throw new Error('Missing product proof');
        if (!request.signal.aborted && this.isConnected) this.applyProof(record.textContent);
      } catch {
        if (!request.signal.aborted) this.hideProof();
      } finally {
        if (!request.signal.aborted && this.isConnected) this.scheduleRefresh();
      }
    }
  });
}
